import { useMemo, useState } from "react";
import { useLocation } from "wouter";
import { X, ArrowRight, ArrowLeft, Check, Rocket } from "lucide-react";
import type { AuthUser } from "../lib/api";

/**
 * v3.5.0: post-registration onboarding guide.
 * A large, role-aware, multi-step walkthrough shown once after sign-up and
 * re-openable from the dashboard. Content adapts to the activities the user
 * chose (worker / client / both). All artwork is inline SVG — no assets.
 */

const PROVIDER_CAPS = ["offer_service", "find_work", "promote_company"];
const CLIENT_CAPS = ["hire", "post_job", "post_project"];

type Slide = {
  key: string;
  kicker: string;
  title: string;
  body: string;
  bullets?: string[];
  art: React.ReactNode;
};

/* ---------------- Inline brand illustrations ---------------- */
const NAVY = "#0f2540", GOLD = "#e6a417", GREEN = "#1f9d57", RED = "#e0313e";

function ArtWelcome() {
  return (
    <svg viewBox="0 0 240 160" className="w-full h-full" role="img" aria-label="Welcome to KontrataPH">
      <circle cx="120" cy="80" r="54" fill="#ffffff" opacity="0.12" />
      <circle cx="120" cy="80" r="38" fill="#ffffff" opacity="0.20" />
      {/* Official KontrataPH logo */}
      <image href="/KontrataPH-Icon.svg" x="84" y="50" width="72" height="60" preserveAspectRatio="xMidYMid meet" />
      <g fill={GOLD}>
        <path d="M54 44 l3 7 7 3 -7 3 -3 7 -3 -7 -7 -3 7 -3z" />
        <path d="M186 96 l2.5 6 6 2.5 -6 2.5 -2.5 6 -2.5 -6 -6 -2.5 6 -2.5z" />
      </g>
      <circle cx="60" cy="120" r="4" fill="#ffffff" opacity="0.6" />
      <circle cx="182" cy="46" r="3" fill="#ffffff" opacity="0.6" />
    </svg>
  );
}
function ArtProfile() {
  return (
    <svg viewBox="0 0 240 160" className="w-full h-full" role="img" aria-label="Profile">
      <rect x="52" y="30" width="136" height="100" rx="12" fill="#ffffff" opacity="0.95" />
      <circle cx="84" cy="60" r="15" fill={NAVY} />
      <rect x="108" y="50" width="66" height="9" rx="4.5" fill={NAVY} opacity="0.85" />
      <rect x="108" y="66" width="46" height="7" rx="3.5" fill={NAVY} opacity="0.4" />
      <rect x="66" y="90" width="108" height="7" rx="3.5" fill={NAVY} opacity="0.25" />
      <rect x="66" y="104" width="80" height="7" rx="3.5" fill={NAVY} opacity="0.25" />
      <g transform="translate(150,96)"><rect width="26" height="26" rx="13" fill={GOLD} /><path d="M8 13 l3.5 3.5 L20 8" stroke={NAVY} strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" /></g>
    </svg>
  );
}
function ArtVerify() {
  return (
    <svg viewBox="0 0 240 160" className="w-full h-full" role="img" aria-label="Verification">
      <path d="M120 24 l60 20 v40 c0 34 -26 52 -60 62 c-34 -10 -60 -28 -60 -62 v-40z" fill="#ffffff" opacity="0.95" />
      <path d="M120 38 l46 16 v32 c0 26 -20 40 -46 48 c-26 -8 -46 -22 -46 -48 v-32z" fill={GREEN} opacity="0.15" />
      <path d="M98 84 l14 14 30 -32" stroke={GREEN} strokeWidth="7" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <g transform="translate(150,30)"><path d="M12 0 l3 8 8 3 -8 3 -3 8 -3 -8 -8 -3 8 -3z" fill={GOLD} /></g>
    </svg>
  );
}
function ArtHire() {
  return (
    <svg viewBox="0 0 240 160" className="w-full h-full" role="img" aria-label="Get hired">
      <rect x="58" y="52" width="124" height="80" rx="12" fill="#ffffff" opacity="0.95" />
      <rect x="94" y="40" width="52" height="20" rx="6" fill="#ffffff" opacity="0.95" />
      <rect x="100" y="46" width="40" height="8" rx="4" fill={NAVY} opacity="0.5" />
      <rect x="58" y="80" width="124" height="4" fill={NAVY} opacity="0.12" />
      <g transform="translate(150,86)"><rect width="26" height="26" rx="6" fill={GOLD} /><path d="M8 13 l3.5 3.5 L20 8" stroke={NAVY} strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" /></g>
      <rect x="74" y="96" width="60" height="7" rx="3.5" fill={NAVY} opacity="0.28" />
      <rect x="74" y="110" width="42" height="7" rx="3.5" fill={NAVY} opacity="0.28" />
    </svg>
  );
}
function ArtPost() {
  return (
    <svg viewBox="0 0 240 160" className="w-full h-full" role="img" aria-label="Post work">
      <rect x="60" y="34" width="120" height="92" rx="12" fill="#ffffff" opacity="0.95" />
      <rect x="76" y="52" width="88" height="9" rx="4.5" fill={NAVY} opacity="0.8" />
      <rect x="76" y="70" width="72" height="7" rx="3.5" fill={NAVY} opacity="0.3" />
      <rect x="76" y="84" width="88" height="7" rx="3.5" fill={NAVY} opacity="0.3" />
      <g transform="translate(150,96)"><circle cx="16" cy="16" r="16" fill={RED} /><path d="M16 8 v16 M8 16 h16" stroke="#fff" strokeWidth="3" strokeLinecap="round" /></g>
    </svg>
  );
}
function ArtCompare() {
  return (
    <svg viewBox="0 0 240 160" className="w-full h-full" role="img" aria-label="Compare providers">
      {[0, 1, 2].map((i) => (
        <g key={i} transform={`translate(${56 + i * 46},44)`}>
          <rect width="38" height="72" rx="9" fill="#ffffff" opacity="0.95" />
          <circle cx="19" cy="20" r="10" fill={NAVY} opacity="0.85" />
          <rect x="8" y="38" width="22" height="6" rx="3" fill={NAVY} opacity="0.3" />
          <g transform="translate(9,50)" fill={GOLD}><path d="M5 0 l1.5 3 3.2 .3 -2.4 2.1 .7 3.1 -3-1.7 -3 1.7 .7-3.1 -2.4-2.1 3.2-.3z" /></g>
        </g>
      ))}
    </svg>
  );
}
function ArtWallet() {
  return (
    <svg viewBox="0 0 240 160" className="w-full h-full" role="img" aria-label="Secure payments">
      <rect x="62" y="44" width="116" height="76" rx="12" fill="#ffffff" opacity="0.95" />
      <rect x="62" y="60" width="116" height="14" fill={NAVY} opacity="0.85" />
      <g transform="translate(150,86)"><circle cx="14" cy="14" r="14" fill={GREEN} /><path d="M8 14 l4 4 8 -9" stroke="#fff" strokeWidth="2.6" fill="none" strokeLinecap="round" strokeLinejoin="round" /></g>
      <text x="78" y="104" fontFamily="Arial" fontWeight="bold" fontSize="14" fill={NAVY}>₱ Escrow</text>
    </svg>
  );
}
function ArtReady() {
  return (
    <svg viewBox="0 0 240 160" className="w-full h-full" role="img" aria-label="Account ready">
      <circle cx="120" cy="80" r="46" fill="#ffffff" opacity="0.95" />
      <path d="M98 80 l16 16 34 -38" stroke={GREEN} strokeWidth="9" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <g fill={GOLD}>
        <path d="M60 44 l3 7 7 3 -7 3 -3 7 -3 -7 -7 -3 7 -3z" />
        <path d="M182 60 l2.5 6 6 2.5 -6 2.5 -2.5 6 -2.5 -6 -6 -2.5 6 -2.5z" />
        <path d="M176 108 l2 5 5 2 -5 2 -2 5 -2 -5 -5 -2 5 -2z" />
      </g>
    </svg>
  );
}

function buildSlides(user: AuthUser): Slide[] {
  const caps = user.capabilities || [];
  const isWorker = caps.some((c) => PROVIDER_CAPS.includes(c));
  const isClient = caps.some((c) => CLIENT_CAPS.includes(c));
  const both = isWorker && isClient;
  const name = user.firstName || "there";

  const welcome: Slide = {
    key: "welcome",
    kicker: "Welcome aboard",
    title: `Welcome to KontrataPH, ${name}!`,
    body: both
      ? "Your account is set up to both hire trusted talent and offer your own services. Here's a quick tour to help you get the most out of it."
      : isWorker
        ? "You've joined the Philippines' marketplace for skilled workers, freelancers and service providers. Let's get you ready to win work."
        : "You've joined the Philippines' marketplace for hiring trusted workers, freelancers and companies. Let's get you ready to hire with confidence.",
    art: <ArtWelcome />,
  };

  const ready: Slide = {
    key: "ready",
    kicker: "You're all set",
    title: "Your account is ready",
    body: "That's it — everything's set up. Jump in and start exploring KontrataPH.",
    bullets: both
      ? ["Post your first job or project", "Complete your worker profile to get hired", "Fund your wallet to hire securely"]
      : isWorker
        ? ["Complete your profile & résumé", "Submit KYC to become hireable", "Browse jobs and send your first bid"]
        : ["Post your first job, project or RFQ", "Review and compare verified providers", "Hire and pay securely through escrow"],
    art: <ArtReady />,
  };

  let middle: Slide[];
  if (isWorker && !isClient) {
    middle = [
      { key: "w1", kicker: "Step 1 · Set up", title: "Build a standout profile", body: "Add your skills, experience, photos and a short video introduction. A complete profile ranks higher in search and wins more work.", art: <ArtProfile /> },
      { key: "w2", kicker: "Step 2 · Get verified", title: "Verify to become hireable", body: "Submit your KYC documents so admins can verify you. Verified workers can be hired and can earn the Star badge for a visibility boost.", art: <ArtVerify /> },
      { key: "w3", kicker: "Step 3 · Win work", title: "Bid, get hired, get paid", body: "Browse jobs, projects and RFQs, then send bids and proposals. When you're hired, work is protected and paid out securely through escrow.", art: <ArtHire /> },
      { key: "wb", kicker: "Why KontrataPH", title: "What you get as a provider", body: "Built to help skilled Filipinos get discovered and paid fairly.",
        bullets: ["Verified-worker badge that builds client trust", "Star badge boost for higher search ranking", "Secure escrow payments and easy cash-out", "Direct messaging with clients, all in one place"], art: <ArtWallet /> },
    ];
  } else if (isClient && !isWorker) {
    middle = [
      { key: "c1", kicker: "Step 1 · Post", title: "Tell us what you need", body: "Post a job, project or RFQ describing the work, budget and timeline. It only takes a couple of minutes to reach thousands of providers.", art: <ArtPost /> },
      { key: "c2", kicker: "Step 2 · Compare", title: "Review verified providers", body: "Receive bids and proposals, then compare profiles, ratings and reviews to shortlist the best fit for your job.", art: <ArtCompare /> },
      { key: "c3", kicker: "Step 3 · Hire safely", title: "Hire and pay through escrow", body: "Hire your chosen provider and fund milestones into escrow. Release payment only when you're happy with the work.", art: <ArtWallet /> },
      { key: "cb", kicker: "Why KontrataPH", title: "What you get as a client", body: "Everything you need to hire with confidence.",
        bullets: ["Access to KYC-verified workers and companies", "Escrow protection on every transaction", "Ratings and reviews to hire with confidence", "Manage jobs, bids and payments in one dashboard"], art: <ArtHire /> },
    ];
  } else {
    // both
    middle = [
      { key: "b1", kicker: "Step 1 · Your profile", title: "Set up your profile", body: "Complete your worker profile — skills, photos and a video intro — so clients can find and hire you. You can post work anytime too.", art: <ArtProfile /> },
      { key: "b2", kicker: "Step 2 · Post or verify", title: "Post work or get verified", body: "Post a job, project or RFQ to hire talent — or submit your KYC to become a verified, hireable provider yourself.", art: <ArtPost /> },
      { key: "b3", kicker: "Step 3 · Transact safely", title: "Hire and get paid securely", body: "Whether you're hiring or getting hired, every deal is protected by escrow, with direct messaging and reviews built in.", art: <ArtWallet /> },
      { key: "bb", kicker: "Why KontrataPH", title: "The best of both sides", body: "One account to hire trusted talent and grow your own work.",
        bullets: ["Hire KYC-verified workers and companies", "Get discovered and earn the Star badge boost", "Secure escrow for hiring and cash-out", "Ratings, reviews and messaging in one place"], art: <ArtCompare /> },
    ];
  }

  return [welcome, ...middle, ready];
}

export function OnboardingGuide({ user, onClose }: { user: AuthUser; onClose: () => void }) {
  const slides = useMemo(() => buildSlides(user), [user]);
  const [i, setI] = useState(0);
  const [, navigate] = useLocation();
  const total = slides.length;
  const s = slides[i];
  const isLast = i === total - 1;

  const finish = () => { onClose(); };
  const startExploring = () => { onClose(); navigate("/marketplace"); };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-navy/60 backdrop-blur-sm" role="dialog" aria-modal="true">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]">
        {/* Hero / graphic */}
        <div className="relative bg-navy px-6 pt-6 pb-8">
          <button onClick={finish} aria-label="Close" className="absolute right-4 top-4 p-1.5 rounded-lg text-white/70 hover:text-white hover:bg-white/10">
            <X className="w-5 h-5" />
          </button>
          <div className="text-brand-gold text-xs font-semibold uppercase tracking-wide mb-3">{s.kicker}</div>
          <div className="h-44 sm:h-52 w-full">{s.art}</div>
          {/* gold accent */}
          <div className="absolute left-0 right-0 bottom-0 h-1 bg-brand-gold" />
        </div>

        {/* Body */}
        <div className="px-6 sm:px-8 py-6 overflow-y-auto">
          <h2 className="text-2xl font-extrabold text-navy mb-2">{s.title}</h2>
          <p className="text-mut text-[15px] leading-relaxed">{s.body}</p>
          {s.bullets && (
            <ul className="mt-4 space-y-2.5">
              {s.bullets.map((b, k) => (
                <li key={k} className="flex items-start gap-2.5 text-ink text-[15px]">
                  <span className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-brand-green/15 text-brand-green flex items-center justify-center">
                    <Check className="w-3.5 h-3.5" />
                  </span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer / controls */}
        <div className="mt-auto border-t border-line px-6 sm:px-8 py-4 flex items-center justify-between gap-3">
          {/* progress dots */}
          <div className="flex items-center gap-1.5">
            {slides.map((_, k) => (
              <span key={k} className={`h-2 rounded-full transition-all ${k === i ? "w-6 bg-brand-gold" : "w-2 bg-line"}`} />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {i > 0 && (
              <button onClick={() => setI((v) => Math.max(0, v - 1))} className="btn-outline h-10 px-4">
                <ArrowLeft className="w-4 h-4" />Back
              </button>
            )}
            {!isLast && i === 0 && (
              <button onClick={finish} className="btn-ghost h-10 px-3 text-mut text-sm">Skip</button>
            )}
            {!isLast ? (
              <button onClick={() => setI((v) => Math.min(total - 1, v + 1))} className="btn-navy h-10 px-5">
                Next<ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button onClick={startExploring} className="btn-navy h-10 px-5">
                <Rocket className="w-4 h-4" />Start exploring
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
